import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const CROPS = [
  {
    id: "soya",
    path: "/soya",
    tag: "Cultivo 01 / Verano",
    title: "Soya",
    img: "/fotos/cultivo-soya-aerea.webp",
    alt: "Campo de soya en hileras visto desde el aire, Santa Cruz",
    body: "Control de roya asiática, chinches y orugas con cobertura foliar pareja, incluso en lotes densos de ciclo tardío.",
    points: ["Roya asiática", "Chinches", "Orugas"],
  },
  {
    id: "sorgo-maiz",
    path: "/sorgo-y-maiz",
    tag: "Cultivo 02 / Invierno",
    title: "Sorgo y maíz",
    img: "/fotos/cultivo-sorgo.webp",
    alt: "Sorgo en panoja madura al atardecer, Santa Cruz",
    body: "Aplicación contra gusano cogollero en ventana crítica, sin esperar pista ni pisotear surcos en campaña de invierno.",
    points: ["Gusano cogollero", "Campaña abr–ago", "Sin pisoteo"],
  },
  {
    id: "cana",
    path: "/cana",
    tag: "Cultivo 03 / Cosecha",
    title: "Caña de azúcar",
    img: "/fotos/cultivo-cana.webp",
    alt: "Campo de caña de azúcar en vista aérea",
    body: "Desecantes y madurantes aplicados a la altura exacta sobre caña alta, donde el equipo terrestre ya no entra.",
    points: ["Desecante", "Madurante", "Caña en pie"],
  },
  {
    id: "siembra",
    path: "/siembra-y-fertilizacion",
    tag: "Servicio 04 / Cobertura",
    title: "Siembra y fertilización",
    img: "/fotos/servicio-siembra.webp",
    alt: "Dron esparciendo semilla granulada sobre surcos arados al atardecer",
    body: "Siembra de cobertura y fertilización foliar con dosificación por hectárea registrada en ficha técnica.",
    points: ["Cobertura", "Foliar", "Dosis registrada"],
  },
  {
    id: "girasol",
    path: null,
    wide: true,
    tag: "Cultivo 05 / Verano e invierno",
    title: "Girasol",
    img: "/fotos/cultivo-girasol.webp",
    alt: "Campo de girasol en floración al atardecer, Chiquitania",
    body: "Fungicidas e insecticidas foliares en girasol de la Chiquitania: cobertura pareja en plena floración sin aplastar una sola planta. El cultivo que más agradece el vuelo bajo.",
    points: ["Floración protegida", "Chiquitania", "Cero pisoteo"],
  },
];

const Servicios = () => (
  <section id="servicios" data-testid="servicios-section" className="bg-[#F6F7F5] text-[#151A1F] py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <motion.p {...reveal} className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#046A38]">
            Soluciones de precisión por cultivo
          </motion.p>
          <motion.h2
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.1 }}
            className="font-display font-bold tracking-tight leading-[1.08] text-2xl sm:text-4xl lg:text-5xl mt-6"
          >
            Cada cultivo tiene su ventana. <br className="hidden sm:block" />
            Nosotros llegamos a tiempo.
          </motion.h2>
        </div>
        <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.15 }} className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#6B7280] max-w-xs">
          Control de plagas · Fertilización foliar · Diagnóstico previo · Ficha firmada
        </motion.p>
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#151A1F]/10 border border-[#151A1F]/10">
        {CROPS.map((c, i) => (
          <motion.article
            key={c.id}
            data-testid={`servicio-card-${c.id}`}
            {...reveal}
            transition={{ ...reveal.transition, delay: i * 0.1 }}
            whileHover={{ rotateX: 1.4, rotateY: -1.4 }}
            style={{ transformPerspective: 1000 }}
            className={`group bg-[#F6F7F5] hover:bg-white transition-colors duration-500 ${c.wide ? "md:col-span-2" : ""}`}
          >
            <div className={c.wide ? "grid sm:grid-cols-2" : ""}>
              <div className={`relative overflow-hidden ${c.wide ? "h-56 sm:h-auto sm:min-h-[300px]" : "h-44 sm:h-52"}`}>
                <img
                  src={c.img}
                  alt={c.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#151A1F]/40 to-transparent" aria-hidden="true" />
                <span className="absolute top-4 left-4 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5] bg-[#151A1F]/65 backdrop-blur-sm px-3 py-1.5">
                  {c.tag}
                </span>
              </div>
              <div className="p-8 sm:p-10">
                <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight group-hover:text-[#046A38] transition-colors duration-300">
                  {c.title}
                </h3>
                <p className="mt-4 text-base text-[#151A1F]/70 leading-relaxed">{c.body}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {c.points.map((p) => (
                    <span key={p} className="font-mono-tech text-[10px] uppercase tracking-[0.15em] border border-[#151A1F]/15 rounded-full px-3 py-1 text-[#151A1F]/60">
                      {p}
                    </span>
                  ))}
                </div>
                {c.path && (
                  <Link
                    data-testid={`servicio-link-${c.id}`}
                    to={c.path}
                    className="mt-6 block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#6B7280] hover:text-[#046A38] transition-colors"
                  >
                    Ver página de {c.title} →
                  </Link>
                )}
                <button
                  data-testid={`servicio-cotizar-${c.id}`}
                  onClick={() => scrollToSection("cotizar")}
                  className="mt-4 inline-flex items-center gap-2 font-display font-semibold text-sm text-[#046A38] hover:gap-3 transition-all duration-300"
                >
                  Cotizar este cultivo <ArrowUpRight size={16} />
                </button>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

export default Servicios;
